import React from 'react';
import { X, Zap, Check, Clock } from 'lucide-react';
import { useLocaleContext } from '../context/LocaleContext';
import { getMinutesUntilReset } from '../utils/scanLimits';

interface PowerUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignup: () => void;
}

const PowerUserModal: React.FC<PowerUserModalProps> = ({
  isOpen,
  onClose,
  onSignup,
}) => {
  const { locale } = useLocaleContext();
  const minutesUntilReset = getMinutesUntilReset();

  if (!isOpen) return null;

  const handleSignupClick = () => {
    onSignup();
  };

  const handleDismiss = () => {
    onClose();
  };

  const content: Record<string, {
    title: string;
    subtitle: string;
    benefitsTitle: string;
    benefits: string[];
    waitText: string;
    signupButton: string;
    waitButton: string;
  }> = {
    en: {
      title: "🚀 Wow, you're a power user!",
      subtitle: "You've used all 5 scans this hour. You're clearly getting value from our tool!",
      benefitsTitle: "Create an account to get:",
      benefits: [
        "Unlimited scans per hour",
        "1,500 character limit (3x more!)",
        "No cooldowns between scans",
        "Access to document cleaning (.docx, .pages)",
        "Priority support"
      ],
      waitText: `Or wait ${minutesUntilReset} minute${minutesUntilReset !== 1 ? 's' : ''} for your limit to reset`,
      signupButton: "Create Account",
      waitButton: "I'll Wait",
    },
    de: {
      title: "🚀 Wow, Sie sind ein Power-User!",
      subtitle: "Sie haben alle 5 Scans dieser Stunde verwendet. Sie holen eindeutig viel aus unserem Tool heraus!",
      benefitsTitle: "Erstellen Sie ein Konto und erhalten Sie:",
      benefits: [
        "Unbegrenzte Scans pro Stunde",
        "1.500 Zeichen-Limit (3x mehr!)",
        "Keine Wartezeiten zwischen Scans",
        "Zugriff auf Dokumentenreinigung (.docx, .pages)",
        "Prioritäts-Support"
      ],
      waitText: `Oder warten Sie ${minutesUntilReset} Minute${minutesUntilReset !== 1 ? 'n' : ''} bis Ihr Limit zurückgesetzt wird`,
      signupButton: "Konto erstellen",
      waitButton: "Ich warte",
    },
    'pt-BR': {
      title: "🚀 Uau, você é um usuário avançado!",
      subtitle: "Você usou todas as 5 varreduras desta hora. Claramente está obtendo valor da nossa ferramenta!",
      benefitsTitle: "Crie uma conta para obter:",
      benefits: [
        "Varreduras ilimitadas por hora",
        "Limite de 1.500 caracteres (3x mais!)",
        "Sem tempo de espera entre varreduras",
        "Acesso à limpeza de documentos (.docx, .pages)",
        "Suporte prioritário"
      ],
      waitText: `Ou aguarde ${minutesUntilReset} minuto${minutesUntilReset !== 1 ? 's' : ''} para seu limite ser resetado`,
      signupButton: "Criar Conta",
      waitButton: "Vou Aguardar",
    },
    'ms-MY': {
      title: "🚀 Wah, anda pengguna kuasa!",
      subtitle: "Anda telah menggunakan semua 5 imbasan jam ini. Anda jelas mendapat manfaat daripada alat kami!",
      benefitsTitle: "Cipta akaun untuk mendapat:",
      benefits: [
        "Imbasan tanpa had setiap jam",
        "Had 1,500 aksara (3x lebih!)",
        "Tiada masa tunggu antara imbasan",
        "Akses kepada pembersihan dokumen (.docx, .pages)",
        "Sokongan keutamaan"
      ],
      waitText: `Atau tunggu ${minutesUntilReset} minit untuk had anda ditetapkan semula`,
      signupButton: "Cipta Akaun",
      waitButton: "Saya Tunggu",
    },
    es: {
      title: "🚀 ¡Vaya, eres un usuario avanzado!",
      subtitle: "Has usado los 5 escaneos de esta hora. ¡Claramente estás obteniendo valor de nuestra herramienta!",
      benefitsTitle: "Crea una cuenta para obtener:",
      benefits: [
        "Escaneos ilimitados por hora",
        "Límite de 1.500 caracteres (¡3x más!)",
        "Sin tiempos de espera entre escaneos",
        "Acceso a limpieza de documentos (.docx, .pages)",
        "Soporte prioritario"
      ],
      waitText: `O espera ${minutesUntilReset} minuto${minutesUntilReset !== 1 ? 's' : ''} para que se restablezca tu límite`,
      signupButton: "Crear Cuenta",
      waitButton: "Esperaré",
    },
    fr: {
      title: "🚀 Impressionnant, vous êtes un utilisateur intensif !",
      subtitle: "Vous avez utilisé vos 5 analyses de cette heure. Vous tirez clairement profit de notre outil !",
      benefitsTitle: "Créez un compte pour obtenir :",
      benefits: [
        "Analyses illimitées par heure",
        "Limite de 1 500 caractères (3x plus !)",
        "Aucun délai d'attente entre les analyses",
        "Accès au nettoyage de documents (.docx, .pages)",
        "Support prioritaire"
      ],
      waitText: `Ou patientez ${minutesUntilReset} minute${minutesUntilReset !== 1 ? 's' : ''} pour la réinitialisation de votre limite`,
      signupButton: "Créer un Compte",
      waitButton: "Je Patiente",
    },
    ja: {
      title: "🚀 すごい、パワーユーザーですね！",
      subtitle: "今時間の5回のスキャンをすべて使用しました。私たちのツールから価値を得ていることは明らかです！",
      benefitsTitle: "アカウントを作成して以下を取得：",
      benefits: [
        "1時間あたり無制限のスキャン",
        "1,500文字制限（3倍！）",
        "スキャン間の待ち時間なし",
        "ドキュメントクリーニングへのアクセス（.docx, .pages）",
        "優先サポート"
      ],
      waitText: `または${minutesUntilReset}分お待ちいただくと制限がリセットされます`,
      signupButton: "アカウントを作成",
      waitButton: "待ちます",
    },
    id: {
      title: "🚀 Luar biasa, Anda pengguna aktif!",
      subtitle: "Anda telah menggunakan semua 5 pemindaian jam ini. Anda jelas mendapat manfaat dari alat kami!",
      benefitsTitle: "Buat akun untuk mendapatkan:",
      benefits: [
        "Pemindaian tanpa batas per jam",
        "Batas 1.500 karakter (3x lebih banyak!)",
        "Tanpa waktu tunggu antar pemindaian",
        "Akses ke pembersihan dokumen (.docx, .pages)",
        "Dukungan prioritas"
      ],
      waitText: `Atau tunggu ${minutesUntilReset} menit untuk batas Anda direset`,
      signupButton: "Buat Akun",
      waitButton: "Saya Tunggu",
    },
  };

  const t = content[locale] || content.en;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fadeIn"
        onClick={handleDismiss}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white dark:bg-charcoal-800 rounded-2xl shadow-2xl dark:shadow-dark-2xl max-w-lg w-full pointer-events-auto transform animate-slideUp border border-neutral-200 dark:border-charcoal-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative p-6 pb-4 border-b border-neutral-200 dark:border-charcoal-700">
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-charcoal-700 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
            </button>

            <div className="pr-8">
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
                {t.title}
              </h2>
              <p className="text-neutral-600 dark:text-neutral-300 text-sm leading-relaxed">
                {t.subtitle}
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="p-6">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                <h3 className="font-semibold text-neutral-900 dark:text-white">
                  {t.benefitsTitle}
                </h3>
              </div>

              <ul className="space-y-2.5">
                {t.benefits.map((benefit, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-sm text-neutral-700 dark:text-neutral-300"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                      </div>
                    </div>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Wait info */}
            {minutesUntilReset > 0 && (
              <div className="mt-4 p-3 bg-neutral-50 dark:bg-charcoal-700/50 rounded-lg border border-neutral-200 dark:border-charcoal-600">
                <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                  <Clock className="w-4 h-4" />
                  <span>{t.waitText}</span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 pt-0 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSignupClick}
              className="flex-1 bg-gradient-to-r from-accent-600 to-purple-600 hover:from-accent-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {t.signupButton}
            </button>
            <button
              onClick={handleDismiss}
              className="flex-1 sm:flex-initial sm:px-6 bg-neutral-100 dark:bg-charcoal-700 hover:bg-neutral-200 dark:hover:bg-charcoal-600 text-neutral-700 dark:text-neutral-300 font-medium py-3 px-6 rounded-xl transition-colors"
            >
              {t.waitButton}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default PowerUserModal;
